/**
 * Power Compatibility Engine
 *
 * Checks power supply compatibility and adequacy including:
 * - Total wattage requirements
 * - Power connector availability
 * - Power efficiency recommendations
 * - Safety margins and peak load calculations
 */

import type {
  CompatibilityChecker,
  CompatibilityResult,
  PowerRequirement,
  CompatibilityContext
} from '../types/CompatibilityResult'
import type { CPUModel } from '../../models/cpu/cpu-model'
import type { GPUModel } from '../../models/gpu/gpu-model'
import type { PSUModel, PSUConnectors } from '../../models/psu/psu-model'
import type { MotherboardModel } from '../../models/motherboard/motherboard-model'
import type { MemoryModel } from '../../models/memory/memory-model'
import type { StorageModel } from '../../models/storage/storage-model'

type SystemComponents = {
  cpu?: CPUModel
  gpu?: GPUModel
  motherboard?: MotherboardModel
  memory?: MemoryModel[]
  storage?: StorageModel[]
}

/**
 * Power Requirement Calculator
 */
export class PowerRequirementCalculator {
  /**
   * Calculate total system power requirements
   */
  calculateSystemPowerRequirement(components: {
    cpu?: CPUModel
    gpu?: GPUModel
    motherboard?: MotherboardModel
    memory?: MemoryModel[]
    storage?: StorageModel[]
    additionalComponents?: { name: string; wattage: number }[]
  }): {
    totalWattage: number
    breakdown: PowerRequirement[]
    peakWattage: number
    recommendedPSUWattage: number
  } {
    const breakdown: PowerRequirement[] = []
    let totalWattage = 0

    // CPU power requirement
    if (components.cpu) {
      const cpuPower = this.calculateCPUPower(components.cpu)
      breakdown.push({
        component: 'cpu',
        requiredWatts: cpuPower.recommended,
        availableWatts: 0,
        adequacy: 'adequate',
        safetyMargin: 0,
        minimumWattage: cpuPower.base,
        recommendedWattage: cpuPower.recommended,
        peakWattage: cpuPower.peak,
        connectorTypes: ['8-pin CPU']
      })
      totalWattage += cpuPower.recommended
    }

    // GPU power requirement
    if (components.gpu) {
      const gpuPower = this.calculateGPUPower(components.gpu)
      breakdown.push({
        component: 'gpu',
        requiredWatts: gpuPower.recommended,
        availableWatts: 0,
        adequacy: 'adequate',
        safetyMargin: 0,
        minimumWattage: gpuPower.base,
        recommendedWattage: gpuPower.recommended,
        peakWattage: gpuPower.peak,
        connectorTypes: gpuPower.connectors
      })
      totalWattage += gpuPower.recommended
    }

    // Motherboard and chipset
    if (components.motherboard) {
      const mbPower = this.calculateMotherboardPower(components.motherboard)
      breakdown.push({
        component: 'motherboard',
        requiredWatts: mbPower,
        availableWatts: 0,
        adequacy: 'adequate',
        safetyMargin: 0,
        minimumWattage: mbPower,
        recommendedWattage: mbPower,
        peakWattage: mbPower * 1.2,
        connectorTypes: ['24-pin ATX']
      })
      totalWattage += mbPower
    }

    // Memory modules
    if (components.memory && components.memory.length > 0) {
      const memoryPower = this.calculateMemoryPower(components.memory)
      breakdown.push({
        component: 'memory',
        requiredWatts: memoryPower,
        availableWatts: 0,
        adequacy: 'adequate',
        safetyMargin: 0,
        minimumWattage: memoryPower,
        recommendedWattage: memoryPower,
        peakWattage: memoryPower * 1.1,
        connectorTypes: []
      })
      totalWattage += memoryPower
    }

    // Storage devices
    if (components.storage && components.storage.length > 0) {
      const storagePower = this.calculateStoragePower(components.storage)
      const storageConnectors = this.getStorageConnectorTypes(components.storage)
      breakdown.push({
        component: 'storage',
        requiredWatts: storagePower,
        availableWatts: 0,
        adequacy: 'adequate',
        safetyMargin: 0,
        minimumWattage: storagePower,
        recommendedWattage: storagePower,
        peakWattage: storagePower * 1.3,
        connectorTypes: storageConnectors
      })
      totalWattage += storagePower
    }

    // Additional components (fans, RGB, etc.)
    if (components.additionalComponents) {
      const additionalPower = components.additionalComponents.reduce((sum, comp) => sum + comp.wattage, 0)
      breakdown.push({
        component: 'additional',
        requiredWatts: additionalPower,
        availableWatts: 0,
        adequacy: 'adequate',
        safetyMargin: 0,
        minimumWattage: additionalPower,
        recommendedWattage: additionalPower,
        peakWattage: additionalPower,
        connectorTypes: ['SATA Power', 'Molex', 'Fan Headers']
      })
      totalWattage += additionalPower
    }

    // Calculate peak wattage (worst case scenario)
    const peakWattage = breakdown.reduce((sum, req) => sum + (req.peakWattage ?? 0), 0)

    // Recommended PSU wattage with 20% headroom
    const recommendedPSUWattage = Math.ceil((totalWattage * 1.2) / 50) * 50 // Round up to nearest 50W

    return {
      totalWattage,
      breakdown,
      peakWattage,
      recommendedPSUWattage
    }
  }

  private calculateCPUPower(cpu: CPUModel): { base: number; recommended: number; peak: number } {
    const baseTDP = cpu.tdp ?? 65
    const maxTDP = baseTDP * 1.5

    return {
      base: baseTDP,
      recommended: maxTDP,
      peak: maxTDP * 1.2 // Account for power spikes
    }
  }

  private calculateGPUPower(gpu: GPUModel): { base: number; recommended: number; peak: number; connectors: string[] } {
    const tdp = gpu.tdp || 150
    const connectors: string[] = []

    // Determine required power connectors based on TDP
    if (tdp > 300) {
      connectors.push('12VHPWR', '16-pin PCIe')
    } else if (tdp > 225) {
      connectors.push('8-pin PCIe', '8-pin PCIe')
    } else if (tdp > 150) {
      connectors.push('8-pin PCIe')
    } else if (tdp > 75) {
      connectors.push('6-pin PCIe')
    }

    return {
      base: tdp * 0.8,
      recommended: tdp,
      peak: tdp * 1.3,
      connectors
    }
  }

  private calculateMotherboardPower(motherboard: MotherboardModel): number {
    // Base motherboard power consumption
    let power = 20

    // Add power for features
    const network = motherboard.network
    if (network?.wifi) power += 5
    if (network?.bluetooth) power += 2

    // Add power for I/O ports
    const usb = motherboard.usb
    power += (usb?.totalUsb3p0 ?? 0) * 2
    power += (usb?.totalUsb2 ?? 0) * 1

    return Math.min(power, 50) // Cap at 50W for motherboard
  }

  private calculateMemoryPower(memory: MemoryModel[]): number {
    return memory.reduce((total, mem) => {
      const basePerModule = mem.type === 'DDR5' ? 5 : 3
      const speedMultiplier = (mem.speed ?? 3200) / 3200
      return total + (basePerModule * speedMultiplier)
    }, 0)
  }

  private getStorageConnectorTypes(storage: StorageModel[]): string[] {
    const connectors: Set<string> = new Set()
    for (const drive of storage) {
      // NVMe M.2 drives get power from the M.2 slot, no separate connector needed
      if (drive.category === 'SATA SSD' || drive.category === 'HDD') {
        connectors.add('SATA Power')
      }
    }
    return [...connectors]
  }

  private calculateStoragePower(storage: StorageModel[]): number {
    return storage.reduce((total, drive) => {
      if (drive.category === 'NVMe SSD') return total + 8
      if (drive.category === 'SATA SSD') return total + 3
      if (drive.category === 'HDD') return total + 10
      return total + 5 // Default
    }, 0)
  }
}

/**
 * PSU Compatibility Checker
 */
export class PSUCompatibilityChecker implements CompatibilityChecker<PSUModel, SystemComponents> {
  private powerCalculator = new PowerRequirementCalculator()

  check(psu: PSUModel, systemComponents: SystemComponents, context?: CompatibilityContext): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    // Calculate system power requirements
    const powerReq = this.powerCalculator.calculateSystemPowerRequirement(systemComponents)

    // Wattage adequacy check
    const wattageResult = this.checkWattageAdequacy(psu, powerReq)
    results.push(wattageResult)

    // Power connector compatibility
    const connectorResult = this.checkPowerConnectors(psu, powerReq)
    if (connectorResult) results.push(connectorResult)

    // Efficiency analysis
    const efficiencyResult = this.analyzeEfficiency(psu, powerReq, context)
    if (efficiencyResult) results.push(efficiencyResult)

    return results
  }

  getScore(psu: PSUModel, systemComponents: SystemComponents): number {
    const powerReq = this.powerCalculator.calculateSystemPowerRequirement(systemComponents)
    const psuWattage = psu.wattage || 0

    const utilizationRatio = powerReq.totalWattage / psuWattage

    if (utilizationRatio > 1.0) return 0  // Insufficient power
    if (utilizationRatio > 0.9) return 40 // Too close to limit
    if (utilizationRatio > 0.8) return 60 // Marginal
    if (utilizationRatio > 0.7) return 80 // Good
    if (utilizationRatio > 0.5) return 100 // Optimal
    if (utilizationRatio > 0.3) return 90 // Good but oversized
    return 75 // Significantly oversized
  }

  private checkWattageAdequacy(psu: PSUModel, powerReq: ReturnType<PowerRequirementCalculator['calculateSystemPowerRequirement']>): CompatibilityResult {
    const psuWattage = psu.wattage || 0
    const margin = psuWattage - powerReq.totalWattage
    const utilizationRatio = powerReq.totalWattage / psuWattage

    if (margin < 0) {
      return {
        level: 'critical',
        category: 'power',
        message: 'PSU電力容量が不足しています',
        details: `必要電力: ${powerReq.totalWattage}W, PSU容量: ${psuWattage}W (${Math.abs(margin)}W不足)`,
        solutions: [
          `${powerReq.recommendedPSUWattage}W以上のPSUを選択`,
          '高効率コンポーネントへの変更を検討',
          'システム構成の見直し'
        ],
        score: 0
      }
    }

    if (utilizationRatio > 0.9) {
      return {
        level: 'warning',
        category: 'power',
        message: 'PSU使用率が高すぎます',
        details: `使用率: ${(utilizationRatio * 100).toFixed(1)}% (推奨: 50-80%)`,
        solutions: [
          'より大容量のPSUを推奨',
          '将来のアップグレードが制限される可能性',
          'ファン騒音が大きくなる可能性'
        ],
        score: this.getScore(psu, { totalWattage: powerReq.totalWattage })
      }
    }

    if (utilizationRatio < 0.3) {
      return {
        level: 'info',
        category: 'power',
        message: 'PSU容量に十分な余裕があります',
        details: `使用率: ${(utilizationRatio * 100).toFixed(1)}% (余裕: ${margin}W)`,
        solutions: [
          '将来のアップグレードに対応可能',
          'より低容量PSUでコスト削減も可能'
        ],
        score: this.getScore(psu, { totalWattage: powerReq.totalWattage })
      }
    }

    return {
      level: 'info',
      category: 'power',
      message: 'PSU電力容量は適切です',
      details: `使用率: ${(utilizationRatio * 100).toFixed(1)}% (余裕: ${margin}W)`,
      score: this.getScore(psu, { totalWattage: powerReq.totalWattage })
    }
  }

  private checkPowerConnectors(psu: PSUModel, powerReq: ReturnType<PowerRequirementCalculator['calculateSystemPowerRequirement']>): CompatibilityResult | null {
    const requiredConnectors: Record<string, number> = {}
    const availableConnectors = psu.connectors ?? {}

    // Count required connectors
    powerReq.breakdown.forEach(req => {
      req.connectorTypes?.forEach(connector => {
        requiredConnectors[connector] = (requiredConnectors[connector] || 0) + 1
      })
    })

    const missingConnectors: string[] = []
    const warnings: string[] = []

    // Check each required connector type
    Object.entries(requiredConnectors).forEach(([connector, required]) => {
      const available = this.getAvailableConnectorCount(availableConnectors, connector)

      if (available < required) {
        missingConnectors.push(`${connector}: ${required}個必要, ${available}個利用可能`)
      } else if (available === required) {
        warnings.push(`${connector}: 余裕なし (${available}個)`)
      }
    })

    if (missingConnectors.length > 0) {
      return {
        level: 'critical',
        category: 'power',
        message: '必要な電源コネクタが不足しています',
        details: missingConnectors.join(', '),
        solutions: [
          '対応コネクタを持つPSUを選択',
          '変換ケーブルの使用を検討（推奨しない）',
          'モジュラーPSUでケーブル追加'
        ],
        score: 0
      }
    }

    if (warnings.length > 0) {
      return {
        level: 'warning',
        category: 'power',
        message: '電源コネクタに余裕がありません',
        details: warnings.join(', '),
        solutions: [
          '将来のアップグレード時に制限される可能性',
          'より多くのコネクタを持つPSUを検討'
        ]
      }
    }

    return null
  }

  private analyzeEfficiency(psu: PSUModel, powerReq: ReturnType<PowerRequirementCalculator['calculateSystemPowerRequirement']>, _context?: CompatibilityContext): CompatibilityResult | null {
    const efficiency = psu.efficiency
    const utilizationRatio = powerReq.totalWattage / (psu.wattage ?? 1)

    if (!efficiency) return null

    // Efficiency recommendations based on usage
    if (utilizationRatio > 0.5 && utilizationRatio < 0.8) {
      if (efficiency.includes('80+ Gold') || efficiency.includes('80+ Platinum') || efficiency.includes('80+ Titanium')) {
        return {
          level: 'info',
          category: 'power',
          message: '効率的な電源使用です',
          details: `効率認証: ${efficiency}, 使用率: ${(utilizationRatio * 100).toFixed(1)}%`,
          solutions: [
            '電気代の節約が期待できます',
            '発熱が少なく静音性に優れます'
          ]
        }
      } else {
        return {
          level: 'info',
          category: 'power',
          message: 'より高効率なPSUで電気代を節約できます',
          details: `現在: ${efficiency}, 推奨: 80+ Gold以上`,
          solutions: [
            '80+ Gold以上の認証PSUを検討',
            '長期的な電気代削減効果'
          ]
        }
      }
    }

    return null
  }

  private getAvailableConnectorCount(connectivity: Partial<PSUConnectors>, connectorType: string): number {
    switch (connectorType) {
      case '24-pin ATX':
        return connectivity.motherboard ? 1 : 0
      case '8-pin CPU':
        return connectivity.cpu?.length ?? 0
      case '6-pin PCIe':
      case '8-pin PCIe':
        return connectivity.pcie?.length ?? 0
      case '12VHPWR':
      case '16-pin PCIe':
        return connectivity.pcie?.filter(c => c.includes('12VHPWR') || c.includes('16-pin')).length ?? 0
      case 'SATA Power':
        return connectivity.sata ?? 0
      case 'Molex':
        return connectivity.molex ?? 0
      default:
        return 0
    }
  }
}

/**
 * Power Compatibility Engine
 * Coordinates all power compatibility checks
 */
export class PowerCompatibilityEngine {
  private powerCalculator = new PowerRequirementCalculator()
  private psuChecker = new PSUCompatibilityChecker()

  checkPowerCompatibility(psu: PSUModel, systemComponents: {
    cpu?: CPUModel
    gpu?: GPUModel
    motherboard?: MotherboardModel
    memory?: MemoryModel[]
    storage?: StorageModel[]
  }, context?: CompatibilityContext): CompatibilityResult[] {
    return this.psuChecker.check(psu, systemComponents, context)
  }

  calculateSystemPowerRequirement(components: {
    cpu?: CPUModel
    gpu?: GPUModel
    motherboard?: MotherboardModel
    memory?: MemoryModel[]
    storage?: StorageModel[]
  }) {
    return this.powerCalculator.calculateSystemPowerRequirement(components)
  }

  getPowerCompatibilityScore(psu: PSUModel, systemComponents: SystemComponents): number {
    return this.psuChecker.getScore(psu, systemComponents)
  }

  generatePowerReport(psu: PSUModel, systemComponents: SystemComponents): {
    adequate: boolean
    score: number
    powerRequirement: ReturnType<PowerRequirementCalculator['calculateSystemPowerRequirement']>
    utilizationRatio: number
    efficiency: string
    recommendations: string[]
    results: CompatibilityResult[]
  } {
    const powerRequirement = this.calculateSystemPowerRequirement(systemComponents)
    const results = this.checkPowerCompatibility(psu, systemComponents)
    const score = this.getPowerCompatibilityScore(psu, systemComponents)

    const psuWattage = psu.wattage || 0
    const utilizationRatio = powerRequirement.totalWattage / psuWattage

    const recommendations: string[] = []
    results.forEach(result => {
      if (result.solutions) {
        recommendations.push(...result.solutions)
      }
    })

    return {
      adequate: score > 60,
      score,
      powerRequirement,
      utilizationRatio,
      efficiency: psu.efficiency || 'Unknown',
      recommendations: [...new Set(recommendations)],
      results
    }
  }
}